import { useOidc, useOidcAccessToken } from '@axa-fr/react-oidc';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { ConnectionStatusOverlay } from 'app/modules/wallforming/components/content-container/ConnectionStatusOverlay';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';

interface HubConnectionContainerProps {
    children: ReactNode;
}

const TIMEOUT_DELAY_MS = 100;

export const HubConnectionContainer = ({ children }: HubConnectionContainerProps) => {
    // MIND: this component renders 2x (but startHubConnection triggers only once)

    const { startHubConnection, stopHubConnection } = usePlacementStore(
        useShallow((state) => ({
            startHubConnection: state.startHubConnection,
            stopHubConnection: state.stopHubConnection
        }))
    );

    const [searchParams] = useSearchParams();
    const { isAuthenticated } = useOidc();
    const { accessToken } = useOidcAccessToken();

    const accessTokenRef = useRef(accessToken);
    const isMountedRef = useRef<boolean>(false);
    const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const sessionguid = useMemo(
        () => searchParams.get('formingsession') ?? EMPTY_GUID,
        [searchParams]
    );

    useEffect(() => {
        accessTokenRef.current = accessToken;
    }, [accessToken]);

    useEffect(() => {
        isMountedRef.current = true;
        clearTimeout(cleanupTimerRef.current);

        if (!isAuthenticated || !accessTokenRef.current) {
            return;
        }

        startHubConnection(sessionguid, () => accessTokenRef.current);

        return () => {
            isMountedRef.current = false;
            cleanupTimerRef.current = setTimeout(() => {
                if (!isMountedRef.current) {
                    stopHubConnection();
                }
            }, TIMEOUT_DELAY_MS);
        };
    }, [isAuthenticated, sessionguid, startHubConnection, stopHubConnection]);

    return (
        <div
            className="relative h-full border border-border-ultra-light overflow-hidden"
            onContextMenu={(e) => e.preventDefault()}
        >
            <ConnectionStatusOverlay />
            {children}
        </div>
    );
};
